import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Card from "@mui/material/Card";
import axios from "axios";
import { CircularProgress } from "@mui/material";
import Grid from "@mui/material/Grid";
import DetectorsOverview from '../tables/DetectorsOverview';
import config from '../../../../config';
import BarTimeValue from '../Charts/BarTimeValue';
import { detectorModelCodes } from 'modules/globals';
import ReactSpeedometer from "react-d3-speedometer";
import { detectorNames } from 'modules/globals';
import Icon from "@mui/material/Icon";
import Tooltip from '@mui/material/Tooltip';
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import MDBox from "components/MDBox";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";
import Steps from "layouts/pages/widgets/components/Steps";
import { DIAGRAM_COLORS } from 'diagrammColors';


const DetectorNetwork = ({ params }) => {
    const { t } = useTranslation();

    const [chartData, setChartData] = useState()
    const [chartDataScae, setChartDataScae] = useState()
    const [chartDataSiemens, setChartDataSiemens] = useState()
    const [chartDataSwarco, setChartDataSwarco] = useState()

    const [error, setError] = useState(false)
    const [validityGause, setValidityGause] = useState()
    const [invalidScae, setInvalidScae] = useState()
    const [invalidSwarco, setInvalidSwarco] = useState()
    const [invalidSiemens, setInvalidSiemens] = useState()



    const flagsTitle = {
        "1": t("dataQuality.flags.1.title"),
        "2": t("dataQuality.flags.2.title"),
        "3": t("dataQuality.flags.3.title"),
        "4": t("dataQuality.flags.4.title"),
        "5": t("dataQuality.flags.5.title"),
        "6": t("dataQuality.flags.6.title"),
    }
    const flagsTooltip = {
        "1": t("dataQuality.flags.1.tooltip"),
        "2": t("dataQuality.flags.2.tooltip"),
        "3": t("dataQuality.flags.3.tooltip"),
        "4": t("dataQuality.flags.4.tooltip"),
        "5": t("dataQuality.flags.5.tooltip"),
        "6": t("dataQuality.flags.6.tooltip"),
    }

    useEffect(() => {
        async function fetchData() {
            try {
                let getUrl = "";
                if (params.detector === "all") {
                    getUrl = config.ENDPOINTS_URL + `/rpc/th_completeness_per_provider_materialized_dynamic?start_time=${params.dates.from}&end_time=${params.dates.to}&&provider_filter={Siemens,SCAE,Swarco}`
                } else if (params.detector === "yunex") {
                    getUrl = config.ENDPOINTS_URL + `/rpc/th_completeness_per_provider_materialized_dynamic?start_time=${params.dates.from}&end_time=${params.dates.to}&&provider_filter={Siemens}`
                } else if (params.detector === "scae") {
                    getUrl = config.ENDPOINTS_URL + `/rpc/th_completeness_per_provider_materialized_dynamic?start_time=${params.dates.from}&end_time=${params.dates.to}&&provider_filter={SCAE}`
                } else {
                    getUrl = config.ENDPOINTS_URL + `/rpc/th_completeness_per_provider_materialized_dynamic?start_time=${params.dates.from}&end_time=${params.dates.to}&&provider_filter={Swarco}`
                }
                const response = await axios(getUrl, {
                    headers: {
                        "Accept-Profile": "detectors"
                    }
                });
                if (params.detector === "all") {
                    const Siemens = response.data.Siemens.map((item) => ({
                        x: new Date(item.ets).toLocaleString('en-GB'), // Format date to be readable
                        y: item.completeness,
                    }));

                    const SCAE = response.data.SCAE.map((item) => ({
                        x: new Date(item.ets).toLocaleString('en-GB'),
                        y: item.completeness,
                    }));
                    const Swarco = response.data.Swarco.map((item) => ({
                        x: new Date(item.ets).toLocaleString('en-GB'), // Format date to be readable
                        y: item.completeness,
                    }));

                    const allTimestampsSet = new Set([
                        ...SCAE.map(item => item.x),
                        ...Siemens.map(item => item.x),
                        ...Swarco.map(item => item.x)
                    ]);
                    const allTimestamps = Array.from(allTimestampsSet).sort();

                    const normalizeData = (dataset, allTimestamps) => {
                        const map = new Map(dataset.map(item => [item.x, item.y]));

                        return allTimestamps.map(timestamp => ({
                            x: timestamp,
                            y: map.has(timestamp) ? map.get(timestamp) : null // or 0 if you prefer
                        }));
                    };
                    const normalizedSCAE = normalizeData(SCAE, allTimestamps);
                    const normalizedSiemens = normalizeData(Siemens, allTimestamps);
                    const normalizedSwarco = normalizeData(Swarco, allTimestamps);

                    const chartD = {
                        datasets: [
                            {
                                label: "SCAE",
                                data: normalizedSCAE,
                                //borderWidth: 0, // Removes the connecting line                            
                                backgroundColor: "rgba(255, 99, 132, 1)", // Point fill color
                                //tension: 0.4, // Line smoothing
                            },

                            {
                                label: "SIEMENS",
                                data: normalizedSiemens,
                                //borderWidth: 0, // Removes the connecting line                            
                                backgroundColor: "rgba(194, 91, 235, 1)", // Point fill color
                                //tension: 0.4, // Line smoothing
                            },
                            {
                                label: "SWARCO",
                                data: normalizedSwarco,
                                //borderWidth: 0, // Removes the connecting line                            
                                backgroundColor: "rgba(75, 192, 192, 1)", // Point fill color
                                //tension: 0.4, // Line smoothing
                            }
                        ],
                    };

                    const chartSiemens = {
                        datasets: [
                            {
                                label: "SIEMENS",
                                data: Siemens,
                                borderWidth: 0, // Removes the connecting line                            
                                backgroundColor: DIAGRAM_COLORS[0], // Point fill color
                                tension: 0.4, // Line smoothing
                            }
                        ]
                    };
                    const chartScae = {
                        datasets: [
                            {
                                label: "SCAE",
                                data: SCAE,
                                borderWidth: 0, // Removes the connecting line                            
                                backgroundColor: DIAGRAM_COLORS[1], // Point fill color
                                tension: 0.4, // Line smoothing
                            }
                        ],
                    };
                    const chartSwarco = {
                        datasets: [
                            {
                                label: "SWARCO",
                                data: Swarco,
                                borderWidth: 0, // Removes the connecting line                            
                                backgroundColor: DIAGRAM_COLORS[2], // Point fill color
                                tension: 0.4, // Line smoothing
                            }
                        ],
                    };
                    setChartData(chartD)
                    setChartDataSiemens(chartSiemens)
                    setChartDataScae(chartScae)
                    setChartDataSwarco(chartSwarco)

                } else if (params.detector === "yunex") {
                    const Siemens = response.data.Siemens.map((item) => ({
                        x: new Date(item.ets).toLocaleString('en-GB'), // Format date to be readable
                        y: item.completeness,
                    }));
                    const chartD = {
                        datasets: [
                            {
                                label: "SIEMENS",
                                data: Siemens,
                                borderWidth: 0, // Removes the connecting line                            
                                backgroundColor: DIAGRAM_COLORS[0], // Point fill color
                                tension: 0.4, // Line smoothing
                            }
                        ]
                    };
                    setChartData(chartD)
                } else if (params.detector === "scae") {
                    const SCAE = response.data.SCAE.map((item) => ({
                        x: new Date(item.ets).toLocaleString('en-GB'), // Format date to be readable
                        y: item.completeness,
                    }));
                    const chartD = {
                        datasets: [
                            {
                                label: "SCAE",
                                data: SCAE,
                                borderWidth: 0, // Removes the connecting line                            
                                backgroundColor: DIAGRAM_COLORS[0], // Point fill color
                                tension: 0.4, // Line smoothing
                            }
                        ]
                    };
                    setChartData(chartD)
                    setChartDataScae(chartD)
                } else {
                    const Swarco = response.data.Swarco.map((item) => ({
                        x: new Date(item.ets).toLocaleString('en-GB'), // Format date to be readable
                        y: item.completeness,
                    }));
                    const chartD = {
                        datasets: [
                            {
                                label: "SWARCO",
                                data: Swarco,
                                borderWidth: 0, // Removes the connecting line                            
                                backgroundColor: DIAGRAM_COLORS[0], // Point fill color
                                tension: 0.4, // Line smoothing
                            }
                        ]
                    };
                    setChartData(chartD)
                    setChartDataSwarco(chartD)
                }
            } catch (err) {
                console.log(err)
                setError(true)
            }
        }

        async function fetchValidityData(detectorModel) {
            try {
                let getUrl = "";
                const modelCode = detectorModelCodes[detectorModel];
                getUrl = config.ENDPOINTS_URL + `/rpc/th_validity_per_provider_materialized?in_start_timestamp=${params.dates.from}&in_end_timestamp=${params.dates.to}&in_detector_model_id=${modelCode}`
                const response = await axios(getUrl, {
                    headers: {
                        "Accept-Profile": "detectors"
                    }
                });
                setValidityGause(response.data)
            } catch (err) {
                console.log(err)
            }
        }

        async function fetchPercentageInvalid() {
            try {
                let getUrl = "";
                getUrl = config.ENDPOINTS_URL + `/rpc/th_validity_per_provider_materialized_all?in_start_timestamp=${params.dates.from}&in_end_timestamp=${params.dates.to}&in_detector_model_id=1`
                const response1 = await axios(getUrl, {
                    headers: {
                        "Accept-Profile": "detectors"
                    }
                });
                setInvalidScae(response1.data)
                getUrl = config.ENDPOINTS_URL + `/rpc/th_validity_per_provider_materialized_all?in_start_timestamp=${params.dates.from}&in_end_timestamp=${params.dates.to}&in_detector_model_id=2`
                const response2 = await axios(getUrl, {
                    headers: {
                        "Accept-Profile": "detectors"
                    }
                });
                setInvalidSiemens(response2.data)
                getUrl = config.ENDPOINTS_URL + `/rpc/th_validity_per_provider_materialized_all?in_start_timestamp=${params.dates.from}&in_end_timestamp=${params.dates.to}&in_detector_model_id=3`
                const response3 = await axios(getUrl, {
                    headers: {
                        "Accept-Profile": "detectors"
                    }
                });
                setInvalidSwarco(response3.data)
            } catch (err) {
                console.log(err)
            }
        }
        fetchData();
        if (params.detector !== "all") {
            fetchValidityData(params.detector)
        } else {
            fetchPercentageInvalid()
        }
    }, [])

    return (
        <Card style={{ marginTop: '3px', padding: '12px', height: '100%' }}>
            <Grid container item xs={12} md={12}>
                <Grid container={true} p={2}>
                    {
                        (params.detector !== "all" || params.location === "Network") &&
                        <div className='rawContainerDiagrams'>
                            {
                                params.detector === "all" ?
                                    <div className='diagrammHeadTitle'>{t("dataQuality.detectorNetwork.completenessPerDataSource")}</div>
                                    :
                                    <div className='diagrammHeadTitle'>{t("dataQuality.metrics.completeness")} {detectorNames[params.detector]}</div>
                            }
                            {chartData ? <div className='detectorDiagramsContainer'><BarTimeValue chartData={chartData} title="" /></div> : <div className='rawContainerCenteredH'><CircularProgress /></div>}
                        </div>
                    }

                    {
                        params.detector !== "all" &&
                        <div className='rawContainerDiagrams'>
                            <div className='diagrammHeadTitle'>{t("dataQuality.detectorNetwork.validityOfNetwork")}</div>
                            {(validityGause && validityGause.length > 0)
                                ? <div className='rawContainerDiagrams'>
                                    {
                                        validityGause.map((item, key) => (
                                            <MDBox key={key}>
                                                <Steps title={flagsTitle[String(item.flag)]} value={item.percentage_invalid.toFixed(2)} symbol="%" />
                                            </MDBox>
                                        ))
                                    }
                                </div>
                                : (validityGause && validityGause.length === 0) ? <span>{t("dataQuality.detectorComp.noData")}</span> : <div className='rawContainerCenteredH'><CircularProgress /></div>}
                        </div>
                    }
                    {
                        params.detector === "all" &&
                        <div className='rawContainerDiagrams'>
                            <div className='diagrammHeadTitle'>{t("dataQuality.detectorNetwork.completenessPerDataSource")}</div>
                            {chartDataSiemens ? <div className='detectorDiagramsContainerRaw'><BarTimeValue chartData={chartDataSiemens} title="" /></div> : <div className='rawContainerCenteredH'><CircularProgress /></div>}
                            {chartDataSwarco ? <div className='detectorDiagramsContainerRaw'><BarTimeValue chartData={chartDataSwarco} title="" /></div> : <div className='rawContainerCenteredH'><CircularProgress /></div>}
                            {chartDataScae ? <div className='detectorDiagramsContainerRaw'><BarTimeValue chartData={chartDataScae} title="" /></div> : <div className='rawContainerCenteredH'><CircularProgress /></div>}
                        </div>
                    }

                    {
                        params.detector === "all" &&
                        <div className='rawContainerDiagrams'>
                            <div className='diagrammHeadTitle'>{t("dataQuality.detectorNetwork.invalidDataAndCoverage")}</div>
                            <div className='rawContainerDiagrams'>
                                {
                                    invalidSiemens &&
                                    <div className="containerDetStatsAll">
                                        <span className='containerDetStatsTitleAll'>{t("dataQuality.detectorNetwork.invalidVendor", { vendor: "SIEMENS" })}</span>
                                        <div className='containerDetStatsBodyAll'>
                                            <div className='statrowAll'>
                                                <span className="valueDetStats">{invalidSiemens[0].total_records}</span>
                                                <span className="labelDetStatsAll">{t("dataQuality.detectorNetwork.totalRecords")}</span>
                                            </div>
                                            <div className='statrowAll'>
                                                <span className="valueDetStats">{invalidSiemens[0].invalid_records}</span>
                                                <span className="labelDetStatsAll">{t("dataQuality.detectorNetwork.invalidCount")}</span>
                                            </div>
                                            <div className='statrowAll'>
                                                <span className="valueDetStats">{invalidSiemens[0].percentage_invalid}%</span>
                                                <span className="labelDetStatsAll">{t("dataQuality.detectorNetwork.percentageInvalid")}</span>
                                            </div>
                                        </div>
                                    </div>
                                }
                                {
                                    invalidSwarco &&
                                    <div className="containerDetStatsAll">
                                        <span className='containerDetStatsTitleAll'>{t("dataQuality.detectorNetwork.invalidVendor", { vendor: "SWARCO" })}</span>
                                        <div className='containerDetStatsBodyAll'>
                                            <div className='statrowAll'>
                                                <span className="valueDetStats">{invalidSwarco[0].total_records}</span>
                                                <span className="labelDetStatsAll">{t("dataQuality.detectorNetwork.totalRecords")}</span>
                                            </div>
                                            <div className='statrowAll'>
                                                <span className="valueDetStats">{invalidSwarco[0].invalid_records}</span>
                                                <span className="labelDetStatsAll">{t("dataQuality.detectorNetwork.invalidCount")}</span>
                                            </div>
                                            <div className='statrowAll'>
                                                <span className="valueDetStats">{invalidSwarco[0].percentage_invalid}%</span>
                                                <span className="labelDetStatsAll">{t("dataQuality.detectorNetwork.percentageInvalid")}</span>
                                            </div>
                                        </div>
                                    </div>
                                }
                                {
                                    invalidScae &&
                                    <div className="containerDetStatsAll">
                                        <span className='containerDetStatsTitleAll'>{t("dataQuality.detectorNetwork.invalidVendor", { vendor: "SCAE" })}</span>
                                        <div className='containerDetStatsBodyAll'>
                                            <div className='statrowAll'>
                                                <span className="valueDetStats">{invalidScae[0].total_records}</span>
                                                <span className="labelDetStatsAll">{t("dataQuality.detectorNetwork.totalRecords")}</span>
                                            </div>
                                            <div className='statrowAll'>
                                                <span className="valueDetStats">{invalidScae[0].invalid_records}</span>
                                                <span className="labelDetStatsAll">{t("dataQuality.detectorNetwork.invalidCount")}</span>
                                            </div>
                                            <div className='statrowAll'>
                                                <span className="valueDetStats">{invalidScae[0].percentage_invalid}%</span>
                                                <span className="labelDetStatsAll">{t("dataQuality.detectorNetwork.percentageInvalid")}</span>
                                            </div>
                                        </div>
                                    </div>
                                }
                                {
                                    (params.detector === "all") &&
                                    <div className="containerDetStatsAllNetwork">
                                        <span className='containerDetStatsTitleAll'>{t("dataQuality.detectorNetwork.networkCoverage")}</span>
                                        <div className='containerNetworkBody'>
                                            <div className='statrowAllNetwork'>
                                                <span className="valueDetStatsNetwork">22.28%</span>
                                                <span className="labelDetStatsAllNetwork">{t("dataQuality.detectorNetwork.networkCoverageDescription")}</span>
                                            </div>
                                        </div>
                                    </div>
                                }
                            </div>
                        </div>
                    }
                </Grid>
            </Grid>
        </Card>
    );
};

export default DetectorNetwork;


/*
<div className='rawContainerDiagrams'>
    <div className='diagrammHeadTitle'>Accuracy per Provider</div>
    <div className="percentage-container">
        <span className="percentage-value">{5}%</span>
        <span className="percentage-label">The value from the detectors deviated by approximately 5% from the ground truth value (TomTom data) at that specific time interval</span>
    </div>
</div>
*/


/*

{
    (params.detector === "all" || params.location === "Network") &&
    <div className='rawContainerDiagrams'>
        {
            params.detector === "all" ?
                <div className='diagrammHeadTitle'>Completeness per Data Source</div>
                :
                <div className='diagrammHeadTitle'>Completeness {detectorNames[params.detector]}</div>
        }
        {chartData ? <div className='detectorDiagramsContainer'><BarTimeValue chartData={chartData} title="" /></div> : <div className='rawContainerCenteredH'><CircularProgress /></div>}
    </div>
}
*/